import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {
    AuthorizationBillItem,
    NotAnActiveClientInput,
    WasClientAssignedToProviderMap
} from "../../domain";
import _ from "lodash";
import {LoadingSpinner} from "common";
import {WasClientAssignedToProviderInput} from "./WasClientAssignedToProviderInput";
import {CommentsInput} from "../../client-report/CommentsInput";
import {LastServiceDateInput} from "./LastServiceDateInput";
import {formatISODateStr} from "common/util/date";

type Props = {
    visible$: Subject<boolean>;
    onFinished: (formValues: NotAnActiveClientInput | null) => Promise<void>;
};

export function ConsultWithCCMNotAnActiveClientFormModal({visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<NotAnActiveClientInput>({
        defaultValues: {}
    });

    const {promiseInProgress} = usePromiseTracker();

    const submitWithTracking = useCallback((formValues: NotAnActiveClientInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Not An Active Client is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: NotAnActiveClientInput) => {
            await submitWithTracking(_.pick(data, [
                'wasClientAssignedToProvider',
                'lastServiceDate',
                'comments'
            ]));
            methods.reset();
            visible$.next(false);
        })();
    }, [methods, visible$, submitWithTracking]);


    return (
        <ReportModal visible={visible} title={'Not An Active Client'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!promiseInProgress && <>
                        <div className={"p-2"}/>
                        <WasClientAssignedToProviderInput sending={promiseInProgress}/>
                        <LastServiceDateInput sending={promiseInProgress}/>
                        <CommentsInput sending={promiseInProgress}/>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={"Cancel"}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                            </div>
                        </div>
                    </>
                    }

                    {promiseInProgress &&
                        <div className="p-12">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}
function fsoResponseDate(items: AuthorizationBillItem[]) {
    const startDate = _.min(items.map(item => item.startDate)) ?? "";
    const endDate = _.max(items.map(item => item.endDate)) ?? "";
    return `${formatISODateStr(startDate)} to ${formatISODateStr(endDate)}`;
}

export function buildNotAnActiveClientDescription(data: NotAnActiveClientInput | null, selectedItems: AuthorizationBillItem[]) {
    if (data === null) {
        return '';
    }

    let res = ` - Was client assigned to Provider? ${WasClientAssignedToProviderMap.get(data.wasClientAssignedToProvider)} \n`;
    if (data.wasClientAssignedToProvider === 'YES' && data.lastServiceDate) {
        res += ` - Last Service Date: ${formatISODateStr(data.lastServiceDate)} \n`
    }
    res += ` - FSO Response for ${fsoResponseDate(selectedItems)} \n`;

    if (data.comments) {
        res += ' - ' + data.comments;
    }

    return res;
}